// Filter + sort options for the /tours and /boats hubs, and the arguments they
// map to on the tours_filtered / boats_filtered RPCs
// (supabase/migrations/20261012120000_hub_filters.sql). The /boat-rental/[city]
// pages use the boat options too, and the /[city]/tours pages the tour ones,
// both applied in the browser (applyBoatFilters / applyTourFilters).
// The /events hub and /[city]/events pages read event_shows_filtered
// (eventFilterArgs): a date window must count every date of a recurring show,
// which only the database has.
//
// Every filter is a fixed set of options rather than free input, and each
// option is a bucket picked from the catalogue's own spread (~⅓ of tours per
// duration bucket, ~¼ of boats per price bucket), so no choice comes back
// empty. The default value of every group means "no filter": a hub whose
// filters are all at their defaults shows its baked seed, unchanged.

import type { Boat } from './boats';
import { getDateRange } from './date-range';
import { durationMinutes } from './city-listings';
import { tourIsPrivate, type Tour } from './tours';

export type TourSort = 'recommended' | 'price_asc' | 'price_desc' | 'reviews';
export type TourDuration = 'any' | 'short' | 'half' | 'full';
export type TourFormat = 'any' | 'group' | 'private';
export type TourPrice = 'any' | 'under100' | '100to300' | '300to800' | 'over800';

export type TourFilters = {
  duration: TourDuration;
  format: TourFormat;
  price: TourPrice;
  sort: TourSort;
};

export const DEFAULT_TOUR_FILTERS: TourFilters = {
  duration: 'any',
  format: 'any',
  price: 'any',
  sort: 'recommended',
};

export type BoatSort = 'recommended' | 'price_asc' | 'price_desc' | 'newest';
export type BoatTypeFilter =
  | 'any'
  | 'motorboat'
  | 'rib'
  | 'yacht'
  | 'sailboat'
  | 'catamaran'
  | 'nolicence';
export type BoatPrice = 'any' | 'under350' | '350to700' | '700to1500' | 'over1500';

export type BoatFilters = {
  type: BoatTypeFilter;
  price: BoatPrice;
  sort: BoatSort;
};

export const DEFAULT_BOAT_FILTERS: BoatFilters = {
  type: 'any',
  price: 'any',
  sort: 'recommended',
};

type Range = { min?: number; max?: number };

export const TOUR_SORTS: TourSort[] = ['recommended', 'price_asc', 'price_desc', 'reviews'];
export const TOUR_FORMATS: TourFormat[] = ['any', 'group', 'private'];

/** Minutes, min exclusive / max inclusive; a variable duration counts as its upper bound. */
export const TOUR_DURATIONS: Record<TourDuration, Range> = {
  any: {},
  short: { max: 180 },
  half: { min: 180, max: 360 },
  full: { min: 360 },
};

/** EUR "from" price per person, min inclusive / max exclusive. */
export const TOUR_PRICES: Record<TourPrice, Range> = {
  any: {},
  under100: { max: 100 },
  '100to300': { min: 100, max: 300 },
  '300to800': { min: 300, max: 800 },
  over800: { min: 800 },
};

export const BOAT_SORTS: BoatSort[] = ['recommended', 'price_asc', 'price_desc', 'newest'];

/**
 * boat_type values behind each type option. Jet skis and houseboats have no
 * option of their own (a handful of listings) and only show under "all".
 */
export const BOAT_TYPES: Record<BoatTypeFilter, string[] | null> = {
  any: null,
  motorboat: ['Motorboat'],
  rib: ['RIB'],
  yacht: ['Motor yacht', 'Sailing yacht'],
  sailboat: ['Sailboat'],
  catamaran: ['Catamaran'],
  nolicence: ['Boat without licence'],
};

/** EUR day rate, min inclusive / max exclusive. */
export const BOAT_PRICES: Record<BoatPrice, Range> = {
  any: {},
  under350: { max: 350 },
  '350to700': { min: 350, max: 700 },
  '700to1500': { min: 700, max: 1500 },
  over1500: { min: 1500 },
};

export function isDefaultFilters<F extends Record<string, string>>(filters: F, defaults: F): boolean {
  return Object.keys(defaults).every((key) => filters[key] === defaults[key]);
}

/** Arguments for the `tours_filtered` RPC. */
export function tourFilterArgs(filters: TourFilters) {
  const duration = TOUR_DURATIONS[filters.duration];
  const price = TOUR_PRICES[filters.price];
  return {
    p_min_minutes: duration.min ?? null,
    p_max_minutes: duration.max ?? null,
    p_private: filters.format === 'any' ? null : filters.format === 'private',
    p_min_price: price.min ?? null,
    p_max_price: price.max ?? null,
    p_sort: filters.sort,
  };
}

export type EventWhen = 'any' | 'today' | 'tomorrow' | 'weekend' | 'week' | 'month';
export type EventPrice = 'any' | 'under25' | '25to40' | 'over40';
export type EventSort = 'soonest' | 'price_asc' | 'price_desc';

export type EventFilters = {
  when: EventWhen;
  /** A category id (`categories` in the app's events-data.ts), or 'all'. */
  category: string;
  price: EventPrice;
  sort: EventSort;
};

export const DEFAULT_EVENT_FILTERS: EventFilters = {
  when: 'any',
  category: 'all',
  price: 'any',
  sort: 'soonest',
};

/** In display order; each maps to getDateRange() (Europe/Paris days), 'any' to the page's own window. */
export const EVENT_WHENS: EventWhen[] = ['any', 'today', 'tomorrow', 'weekend', 'week', 'month'];
export const EVENT_SORTS: EventSort[] = ['soonest', 'price_asc', 'price_desc'];

/**
 * EUR "from" ticket price, min inclusive / max exclusive. Ticket prices sit in
 * a narrow band (quartiles ~€25 / €35 / €42), hence three buckets.
 */
export const EVENT_PRICES: Record<EventPrice, Range> = {
  any: {},
  under25: { max: 25 },
  '25to40': { min: 25, max: 40 },
  over40: { min: 40 },
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Arguments for the `event_shows_filtered` RPC
 * (supabase/migrations/20261015120000_event_shows_filtered.sql). Dates are
 * computed at call time, in the browser — never at build time. 'any' spans
 * the page's own list: from today, `anyUntilDays` ahead (the hub's 180, a
 * city page's 730).
 */
export function eventFilterArgs(filters: EventFilters, city: string | null, anyUntilDays: number) {
  const range =
    filters.when === 'any'
      ? (() => {
          const { from } = getDateRange('alltime');
          return { from, until: new Date(from.getTime() + anyUntilDays * DAY_MS) };
        })()
      : getDateRange(filters.when);
  const price = EVENT_PRICES[filters.price];
  return {
    p_city: city,
    p_from: range.from.toISOString(),
    p_until: range.until.toISOString(),
    p_category: filters.category === 'all' ? null : filters.category,
    p_min_price: price.min ?? null,
    p_max_price: price.max ?? null,
    p_sort: filters.sort,
  };
}

/** Nulls last in both directions, like the SQL's `nulls last`. */
function compareNullsLast(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a == null || b == null) return a == null ? (b == null ? 0 : 1) : -1;
  return (a - b) * dir;
}

/**
 * The tours_filtered rules applied in the browser, for a list that's already
 * fully loaded (the /[city]/tours seed: the whole city). `tours` must be in
 * the RPC's default order (tour_rank_score, review_count, id): the sort is
 * stable, so ties keep it, exactly like the SQL's fallback order.
 */
export function applyTourFilters(tours: Tour[], filters: TourFilters): Tour[] {
  const duration = TOUR_DURATIONS[filters.duration];
  const price = TOUR_PRICES[filters.price];
  const rows = tours.filter((tour) => {
    const minutes = filters.duration === 'any' ? null : durationMinutes(tour.duration);
    return (
      (filters.duration === 'any' ||
        (minutes != null &&
          (duration.min == null || minutes > duration.min) &&
          (duration.max == null || minutes <= duration.max))) &&
      (filters.format === 'any' || tourIsPrivate(tour) === (filters.format === 'private')) &&
      (price.min == null || (tour.from_price != null && tour.from_price >= price.min)) &&
      (price.max == null || (tour.from_price != null && tour.from_price < price.max))
    );
  });
  switch (filters.sort) {
    case 'price_asc':
      return [...rows].sort((a, b) => compareNullsLast(a.from_price, b.from_price, 1));
    case 'price_desc':
      return [...rows].sort((a, b) => compareNullsLast(a.from_price, b.from_price, -1));
    case 'reviews':
      return [...rows].sort((a, b) => compareNullsLast(a.review_count, b.review_count, -1));
    default:
      return rows;
  }
}

/**
 * The boats_filtered rules applied in the browser, for a list that's already
 * fully loaded (one city's fleet). `boats` must be in the RPC's default order
 * (has_owner_text first, rating desc, id): the sort is stable, so ties keep
 * it, exactly like the SQL's fallback order.
 */
export function applyBoatFilters<B extends Pick<Boat, 'boat_type' | 'from_price' | 'year'>>(
  boats: B[],
  filters: BoatFilters,
): B[] {
  const types = BOAT_TYPES[filters.type];
  const { min, max } = BOAT_PRICES[filters.price];
  const rows = boats.filter(
    (boat) =>
      (types == null || (boat.boat_type != null && types.includes(boat.boat_type))) &&
      (min == null || (boat.from_price != null && boat.from_price >= min)) &&
      (max == null || (boat.from_price != null && boat.from_price < max)),
  );
  if (filters.sort === 'recommended') return rows;
  const key = (boat: B): number | null =>
    filters.sort === 'newest' ? boat.year : boat.from_price;
  const dir = filters.sort === 'price_asc' ? 1 : -1;
  return [...rows].sort((a, b) => compareNullsLast(key(a), key(b), dir));
}

/** Arguments for the `boats_filtered` RPC. */
export function boatFilterArgs(filters: BoatFilters) {
  const price = BOAT_PRICES[filters.price];
  return {
    p_types: BOAT_TYPES[filters.type],
    p_min_price: price.min ?? null,
    p_max_price: price.max ?? null,
    p_sort: filters.sort,
  };
}
